import { useMemo } from 'react';
import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

import { DATE_PARAM_FORMAT } from './useResultsListFilters';
import styles from './ResultsDateRangeField.module.scss';

interface ResultsDateRangeFieldProps {
  ariaLabel: string;
  /** `YYYY-MM-DD` bounds — see DATE_PARAM_FORMAT. */
  from?: string;
  to?: string;
  onChange: (from?: string, to?: string) => void;
  className?: string;
}

/** The date-range control shared by both Results filter bars. */
export function ResultsDateRangeField({ ariaLabel, from, to, onChange, className }: ResultsDateRangeFieldProps) {
  // Either bound can be set on its own (allowEmpty below), so each side is
  // shown independently — a one-sided range from a shared URL stays visible.
  const dateRange = useMemo<[Dayjs | null, Dayjs | null] | null>(
    () => (from || to ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null),
    [from, to],
  );

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const [start, end] = dates ?? [null, null];
    onChange(start?.format(DATE_PARAM_FORMAT), end?.format(DATE_PARAM_FORMAT));
  };

  return (
    <DatePicker.RangePicker
      aria-label={ariaLabel}
      value={dateRange}
      onChange={handleDateRangeChange}
      allowClear
      allowEmpty={[true, true]}
      placeholder={['From', 'To']}
      inputReadOnly
      classNames={{ popup: { root: styles.datePopup } }}
      className={className}
    />
  );
}
